import { Component, OnInit, ViewChild } from '@angular/core';
import { BsDropdownDirective, BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { AsyncPipe, NgClass, NgForOf, NgIf, SlicePipe } from '@angular/common';
import { takeUntil } from 'rxjs';
import { ofType } from '@ngrx/effects';

import { CommonComponent } from '@components/common.component';
import { SimplebarAngularModule } from 'simplebar-angular';
import { notificationsActions, selectNotifications } from '@redux/notifications.redux';
import { eventsActions } from '@redux/events.effects';
import type { Notification } from '@models/community/notification.model';
import { CalendarEventDetailsComponent } from '@calendar/components/calendar-event-details/calendar-event-details.component';
import { CommunityEventNotificationChromePipe } from '@notifications/pipes/community-event-notification-chrome.pipe';

@Component({
  selector: 'app-notifications-dashboard',
  standalone: true,
  imports: [
    BsDropdownModule,
    AsyncPipe,
    NgForOf,
    NgIf,
    SlicePipe,
    NgClass,
    SimplebarAngularModule,
    CommunityEventNotificationChromePipe,
  ],
  templateUrl: './notifications-dashboard.component.html',
  styleUrls: ['./notifications-dashboard.component.scss'],
})
export class NotificationsDashboardComponent extends CommonComponent implements OnInit {
  @ViewChild('dropdownNotifications') dropdown!: BsDropdownDirective;

  notifications$ = this.store.select(selectNotifications.notifications);
  unread$ = this.store.select(selectNotifications.unread);
  unreadTotal$ = this.store.select(selectNotifications.unreadTotal);

  onlyUnread = false;
  isNotificationsOpen = false;
  unreadTotal = 0;
  private pendingEventDetails = false;

  ngOnInit(): void {
    this.store.dispatch(notificationsActions.isSupported());
    this.store.dispatch(notificationsActions.notifications());

    this.unreadTotal$.subscribe((total) => {
      this.unreadTotal = total ?? 0;
    });

    this.subscriptions.push(
      this.actions$
        .pipe(ofType(eventsActions.detailsSuccess), takeUntil(this.destroy$))
        .subscribe(({ event }) => {
          if (!this.pendingEventDetails) {
            return;
          }
          this.pendingEventDetails = false;
          if (!event) {
            return;
          }
          this.modal.show(CalendarEventDetailsComponent, {
            initialState: { data: event },
            backdrop: 'static',
            class: 'modal-dialog-centered',
          });
        }),
    );
  }

  get notificationsAriaLabel(): string {
    const action = this.isNotificationsOpen ? 'Close notifications' : 'Open notifications';
    const state = this.isNotificationsOpen ? 'expanded' : 'collapsed';
    return `${action}, ${this.unreadTotal} unread, button, ${state}`;
  }

  getNotifications() {
    return this.onlyUnread ? this.unread$ : this.notifications$;
  }

  onDetails(item: Notification) {
    if (item.type !== 'CommunityEvent' || !item.model?.id) {
      return;
    }
    if (!item.readInd) {
      this.store.dispatch(notificationsActions.asRead({ model: item }));
    }
    this.pendingEventDetails = true;
    this.dropdown?.hide();
    this.store.dispatch(eventsActions.details({ id: item.model.id }));
  }

  onNotifications() {
    const path = this.role === 'COMMUNITY_RELATIVE' ? '/family' : '/';
    this.router.navigate([path, 'notifications']).then();
    this.dropdown.hide();
  }

  onDropdownShown(): void {
    this.isNotificationsOpen = true;
  }

  onDropdownHidden(): void {
    this.isNotificationsOpen = false;
  }
}
